import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

/**
 * How this category will look on the parent app's Home screen.
 *
 * Owner, 2026-10-19 (punchlist 2): "this is regarding the category edit form in
 * alfred. can we add a preview of how these fields will look in the parent
 * app." Until now the only feedback after filling this form in was to open the
 * app and look, so a photo with the wrong crop or a name too long for the tile
 * was found by a parent rather than by the person typing it.
 *
 * It is drawn from at-home-app/src/home/SubjectCard.tsx, field for field:
 * full-bleed photo 190px tall, then a 2x2 block whose first row is the
 * duration and the fee and whose second row is the subject name and the
 * button. A coming-soon card is faded, shows NO price and does not open.
 *
 * WHAT IT DOES NOT KNOW, and says so rather than inventing:
 *   - the FEE. It is not on this form. The app reads it from the cheapest open
 *     trial slot for the subject (Schedules listed), so it can change without
 *     this form changing.
 *   - the BUILT-IN PHOTO. With `hero_url` blank the app falls back to a photo
 *     bundled in the app for the subjects we have photographed, and to a
 *     tinted glyph square for the rest. Neither lives in Alfred, so the
 *     preview marks the space instead of guessing which one appears.
 *   - the DURATION, which is a property of each listed session, not of the
 *     category. 60 is what almost every At-Home session runs, so it stands in
 *     as a shape for the row and is labelled as typical.
 *
 * Saying "roughly" in the heading is deliberate. A preview that claimed to be
 * exact would be a worse lie than no preview, because it would be believed.
 */

const useStyles = makeStyles((theme) => ({
  root: { position: 'sticky', top: 0 },
  heading: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: 6
  },
  // The phone the card sits in, so the 190px photo reads at the width a parent
  // actually sees rather than stretched across the dialog.
  phone: {
    width: 300,
    maxWidth: '100%',
    background: '#FFF7F2',
    borderRadius: 20,
    padding: 12,
    border: '1px solid #EADFD6'
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  cardSoon: { opacity: 0.55 },
  photo: {
    width: '100%',
    height: 150,
    objectFit: 'cover',
    display: 'block',
    background: '#F0E7E0'
  },
  photoNone: {
    width: '100%',
    height: 150,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '0 18px',
    boxSizing: 'border-box',
    background: '#F3EAE3',
    color: '#9A8577',
    fontSize: 11,
    lineHeight: 1.35
  },
  tx: {
    padding: '12px 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12
  },
  col1: { minWidth: 0 },
  mins: { fontSize: 12, color: '#9A8577', height: 18, lineHeight: '18px' },
  subject: {
    fontSize: 17,
    fontWeight: 700,
    color: '#3D2B1F',
    lineHeight: '20px',
    wordBreak: 'break-word'
  },
  col2: { textAlign: 'center', flexShrink: 0 },
  fee: { fontSize: 12, color: '#9A8577', height: 18, lineHeight: '18px' },
  feeUnknown: { fontStyle: 'italic', color: '#B9A89B' },
  pill: {
    background: '#FF6B4A',
    color: '#fff',
    borderRadius: 999,
    padding: '7px 16px',
    fontSize: 13,
    fontWeight: 700,
    display: 'inline-block'
  },
  pillSoon: { background: '#E6DAD0', color: '#7A6757' },
  note: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 10,
    lineHeight: 1.45
  },
  hidden: {
    fontSize: 12,
    color: '#B4461F',
    background: '#FDEEE8',
    borderRadius: 8,
    padding: '8px 10px',
    lineHeight: 1.4
  },
  trialBlock: { marginTop: 16 },
  trialCard: {
    background: '#fff',
    border: '1px solid #EADFD6',
    borderRadius: 12,
    padding: '10px 12px'
  },
  trialLabel: { fontSize: 11, color: '#9A8577' },
  trialName: { fontSize: 14, fontWeight: 700, color: '#3D2B1F' }
}))

/** 'upcoming' is the category spelling of coming-soon; modules say 'coming_soon'. */
const isSoon = (status) => status === 'upcoming'

const CategoryPreview = ({ values }) => {
  const classes = useStyles()
  const name = (values.name || '').trim()
  const photo = (values.hero_url || '').trim()
  const trialName = (values.trial_name || '').trim()
  const status = values.status || 'open'
  const soon = isSoon(status)

  if (status === 'inactive') {
    return (
      <div className={classes.root}>
        <div className={classes.heading}>On the parent&apos;s Home screen</div>
        <div className={classes.hidden}>
          Inactive, so this subject does not appear in the app at all — no card
          on Home, and nothing to book. Set it to Open or Upcoming to see it
          here.
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.heading}>On the parent&apos;s Home screen</div>
      <div className={classes.phone}>
        <div className={`${classes.card} ${soon ? classes.cardSoon : ''}`}>
          {photo ? (
            <img src={photo} alt="" className={classes.photo} />
          ) : (
            <div className={classes.photoNone}>
              the app&apos;s own photo for this subject
              <br />
              (set a photo above to replace it)
            </div>
          )}
          <div className={classes.tx}>
            <div className={classes.col1}>
              <div className={classes.mins}>60 mins</div>
              <div className={classes.subject}>{name || 'Category name'}</div>
            </div>
            <div className={classes.col2}>
              <div className={classes.fee}>
                {soon ? '' : <span className={classes.feeUnknown}>fee</span>}
              </div>
              <div
                className={`${classes.pill} ${soon ? classes.pillSoon : ''}`}
              >
                {soon ? 'Coming soon' : 'Book'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ONLY THE "Upcoming" NOTE SURVIVES (owner, 2026-10-22). The other half
          of this ternary explained where the card's price comes from -- the
          cheapest open trial time under Schedules listed, not this form. It
          went; a bookable card now carries no note at all. The upcoming one
          stays because it describes something the preview cannot show: that
          the real card is faded and does not open when tapped. */}
      {soon ? (
        <div className={classes.note}>
          Upcoming: the card is faded, shows no price, and does not open when
          tapped.
        </div>
      ) : null}

      {trialName ? (
        <div className={classes.trialBlock}>
          <div className={classes.heading}>
            On the booked trial&apos;s session card
          </div>
          <div className={classes.trialCard}>
            <div className={classes.trialLabel}>Trial class</div>
            <div className={classes.trialName}>{trialName}</div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

CategoryPreview.propTypes = { values: PropTypes.object.isRequired }

export default CategoryPreview
